import { React, useContext, useEffect, useMemo } from "react";
import Chart from "react-apexcharts";
import "../../translator";
import { useTranslation } from "react-i18next";
import { generateDatabaseDateTimeENUTC } from "../../commons/FormatearFecha";
import { translateMachineName } from "../../commons/utiles";
import { GeneralChartsContext } from "../../context/GeneralCharts";
import { GeneralMinaContext } from "../../context/GeneralMina";

const paragraphWrapper = (text, color = "#8B8595") => {
  return `<p style="color: ${color};">${text}</p>`;
}

const colors = {
  "encendido": "#01b7c4",
  "apagado": "#c5283d",
  "operativo": "#01b7c4",
  "ralenti": "#e9724c",
  "en_mantencion": "#ffbf00",
  "mantencion": "#ffbf00",
  "sin_info": "#d6dce4",
  "fuera_de_servicio": "#c5283d",
  "justificado": "#28B463",
}

const translateLasChispas = {
  "operativo": "encendido",
  "fuera_de_servicio": "apagado"
};

const orderCategoriesLasChispas = [
  "horometro_jumbo",
  "brazo1_motor",
  "brazo2_motor",
  "brazo1_percutor",
  "brazo2_percutor",
  "mantencion"
];

function getCategoriesLasChispas(series) {
  const categoriesFromSeries = Array.from(
    new Set(series.flatMap((item) => item.data.map((value) => value.x)))
  );

  const categoriesLasChispas = categoriesFromSeries.sort((a, b) => {
    return (
      orderCategoriesLasChispas.indexOf(a) -
      orderCategoriesLasChispas.indexOf(b)
    );
  });

  return categoriesLasChispas;
}

const translatorList = [
  "horometro_jumbo",
  "brazo_1",
  "brazo_2",
  "horometro",
  "arm1_motor",
  "arm1_percutor",
  "arm2_motor",
  "arm2_percutor",
  "brazo1_motor",
  "brazo2_motor",
  "brazo1_percutor",
  "brazo2_percutor",
  "horometro_bolter",
  "brazo1_compresor",
  "brazo2_compresor",
  "mantencion"
];

const HorizontalTimeChart = ({
  series,
  turno,
  numCategorias,
  abrirModalJustificaciones = undefined,
  idMina,
  MostrarLabelCompleto = false,
  fullHeight = false,
  maxWidthYLabel = 185
}) => {
  const { t, i18n } = useTranslation();
  const altoGrafico = 60 + 55 * numCategorias;
  const { onHandleGetGeneralData } = useContext(GeneralMinaContext);
  const { horoumeterLimits, onHandleSetHoroumeterLimits } =
    useContext(GeneralChartsContext);

  useEffect(() => {
    onHandleSetHoroumeterLimits(turno?.min?.getTime(), turno?.max?.getTime());
  }, [turno?.min, turno?.max]);

  const categoriesLasChispas = getCategoriesLasChispas(series);

  const options = useMemo(() => {
    return {
      states: {
        hover: {
          filter: {
            type: "darken",
            value: 0.8
          }
        }
      },
      legend: {
        show: false
      },
      colors: [
        (series) => {
          const { w, seriesIndex, dataPointIndex } = series;
          const draft = w.config.series;

          const currentState = draft[seriesIndex]?.name;
          const machinePart = draft[seriesIndex].data[dataPointIndex]?.x;

          if (
            currentState === "operativo" &&
            idMina === 24 &&
            machinePart === "horometro_jumbo"
          ) {
            return "#52b788";
          }

          return "#01b7c4";
        }
      ],
      chart: {
        type: "rangeBar",
        // sparkline:{enabled:true},
        // parentHeightOffset: 0,
        toolbar: { show: false },
        zoom: { enabled: false },
        defaultLocale: i18n?.language ? i18n.language : "en",
        locales: [
          {
            name: "en",
            options: {
              months: [
                "Enero",
                "Febrero",
                "Marzo",
                "Abril",
                "Mayo",
                "Junio",
                "Julio",
                "Agosto",
                "Septiembre",
                "Octubre",
                "Noviembre",
                "Diciembre"
              ],
              shortMonths: [
                "Ene",
                "Feb",
                "Mar",
                "Abr",
                "May",
                "Jun",
                "Jul",
                "Ago",
                "Sep",
                "Oct",
                "Nov",
                "Dic"
              ],
              days: [
                "Domingo",
                "Lunes",
                "Martes",
                "Miércoles",
                "Jueves",
                "Viernes",
                "Sábado"
              ],
              shortDays: ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"],
              toolbar: {
                download: "Descargar SVG",
                selection: "Selección",
                selectionZoom: "Selección Zoom",
                zoomIn: "Zoom",
                zoomOut: "Alejar",
                pan: "Panning",
                reset: "Reset Zoom"
              }
            }
          },
          {
            name: "es",
            options: {
              months: [
                "Enero",
                "Febrero",
                "Marzo",
                "Abril",
                "Mayo",
                "Junio",
                "Julio",
                "Agosto",
                "Septiembre",
                "Octubre",
                "Noviembre",
                "Diciembre"
              ],
              shortMonths: [
                "Ene",
                "Feb",
                "Mar",
                "Abr",
                "May",
                "Jun",
                "Jul",
                "Ago",
                "Sep",
                "Oct",
                "Nov",
                "Dic"
              ],
              days: [
                "Domingo",
                "Lunes",
                "Martes",
                "Miércoles",
                "Jueves",
                "Viernes",
                "Sábado"
              ],
              shortDays: ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"],
              toolbar: {
                download: "Descargar SVG",
                selection: "Selección",
                selectionZoom: "Selección Zoom",
                zoomIn: "Zoom",
                zoomOut: "Alejar",
                pan: "Panning",
                reset: "Reset Zoom"
              }
            }
          }
        ],
        events: {
          dataPointSelection: (event, chartContext, config) => {
            if (abrirModalJustificaciones) {
              const seriesIndex = config.seriesIndex;
              const dataPointIndex = config.dataPointIndex;
              const selectedData =
                chartContext.w.config.series[seriesIndex].data[dataPointIndex];
              // Aquí puedes acceder a la fecha de inicio y fecha de terminación
              const startDate = new Date(selectedData.y[0]);
              const endDate = new Date(selectedData.y[1]);

              const id_tipo_movimiento = selectedData.id_tipo_movimiento || 0;
              const id_t_registro_movimiento =
                selectedData.id_t_registro_movimiento || 0;
              const id_maquina = selectedData.id_maquina;
              const justificado = selectedData.justificado;

              //si el tipo de movimiento es mantencion, fuera de servicio, detendido, ralenti
              if (
                (id_tipo_movimiento == 2 ||
                  id_tipo_movimiento == 3 ||
                  id_tipo_movimiento == 4 ||
                  id_tipo_movimiento == 5) &&
                !justificado
              ) {
                abrirModalJustificaciones(
                  id_tipo_movimiento,
                  generateDatabaseDateTimeENUTC(startDate),
                  generateDatabaseDateTimeENUTC(endDate),
                  id_t_registro_movimiento,
                  id_maquina
                );
              }
            }
          }
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: fullHeight ? altoGrafico : "85%", //'85%',
          rangeBarGroupRows: true
        }
      },
      grid: {
        show: false
      },
      xaxis: {
        categories: idMina === 24 ? categoriesLasChispas : [],
        min: horoumeterLimits?.min
          ? horoumeterLimits?.min
          : turno.min?.getTime(),
        max: horoumeterLimits?.max
          ? horoumeterLimits?.max
          : turno.max?.getTime(),
        axisTicks: {
          show: false
        },
        axisBorder: {
          show: false
        },
        type: "datetime",
        /* categories: fechas, */
        labels: {
          datetimeUTC: false,
          format: "HH:mm",
          style: {
            fontSize: "12px"
          }
        }
      },
    
      yaxis: {
        axisBorder: {
          show: false
        },
        labels: {
          maxWidth: maxWidthYLabel,
          minWidth: 72,
          style: {
            fontSize: "12px"
          },
          formatter: (val) => {
            if (typeof val === "string") {
              if (
                translatorList.includes(val.toLowerCase().replace(" ", "_"))
              ) {
                const result = t(val.toLowerCase().replace(" ", "_"));
                const index = result.indexOf(" "); // Encuentra el primer espacio en la cadena
                const resultEnArray = [
                  result.substring(0, index),
                  result.substring(index + 1)
                ];
                return resultEnArray;
              } else if (val.length > 9) {
                const nombreSplitted = val.split(" ");
                const nombreMaquina = [
                  nombreSplitted[0],
                  nombreSplitted.slice(1).join(" ")
                ];
                return nombreMaquina;
              } else {
                return translateMachineName(
                  val,
                  t,
                  onHandleGetGeneralData().idMina
                );
              }
            }

            return val;
          }
        }
      },
      fill: {
        type: "solid"
      },
      tooltip: {
        enabled: true,
        x: {
          format: "HH:mm"
        },
        y: {
          title: {
            formatter: (seriesName, objj) => {
              const { seriesIndex, dataPointIndex, w } = objj;

              const draft = w.config.series;

              const currentState = draft[seriesIndex]?.name;
              const machinePart = draft[seriesIndex].data[dataPointIndex]?.x;

              if (
                currentState === "operativo" &&
                machinePart === "horometro_jumbo" &&
                idMina === 24
              ) {
                return paragraphWrapper(t("motor_diesel"), "#52b788");
              }

              if (
                currentState === "operativo" &&
                (machinePart === "brazo1_percutor" ||
                  machinePart === "brazo2_percutor") &&
                idMina === 24
              ) {
                return paragraphWrapper(t("percusion"), "#01b7c4");
              }

              if (seriesName == "justificado") {
                const grupo_justificacion =
                  objj.w.config.series[objj.seriesIndex].data[
                    objj.dataPointIndex
                  ].justificacion;

                return `
                Justificado<br />
                ${grupo_justificacion ? "Tipo: " + grupo_justificacion + "<br />" : ""}
              `;
              }

              if (
                [
                  "operativo",
                  "ralenti",
                  "en_mantencion",
                  "sin_info",
                  "fuera_de_servicio",
                  "horometro",
                  "justificado",
                  "encuentro"
                ].includes(seriesName)
              )
                return idMina === 24 &&
                  (seriesName === "operativo" ||
                    seriesName === "fuera_de_servicio")
                  ? paragraphWrapper(t(translateLasChispas[seriesName]), colors[translateLasChispas[seriesName]])
                  : paragraphWrapper(t(seriesName), colors[seriesName]);

              return seriesName;
            }
          }
        }
      }
    };
  }, [horoumeterLimits, turno, i18n.language]);

  return (
    <div
      id="rangetimebar"
      style={{ marginLeft: MostrarLabelCompleto ? "15px" : "0px" }}>
      <Chart
        height={altoGrafico}
        width={"100%"}
        options={options}
        series={series}
        type="rangeBar"
      />
    </div>
  );
};

export default HorizontalTimeChart;
